import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { isAxiosError } from "axios";
import { CalendarDays, Edit3, FileClock, LayoutGrid, Loader2, Plus, RefreshCw, Save, Search, Trash2, Users, X } from "lucide-react";
import { CalidadControlModificacionDeleteModal } from "../../../components/control-modificaciones/CalidadControlModificacionDeleteModal";
import { CalidadControlModificacionFormModal } from "../../../components/control-modificaciones/CalidadControlModificacionFormModal";
import { CalidadControlModificacionesTable } from "../../../components/control-modificaciones/CalidadControlModificacionesTable";
import { Iso900114001MasterDetailTable } from "../../../components/shared/Iso900114001MasterDetailTable";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001ScrollableCell } from "../../../components/shared/Iso900114001ScrollableCell";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import type { CalidadControlModificacion, CalidadControlModificacionPayload } from "../../../types/control-modificaciones";
import {
  createCalidadDafoAnalisis,
  createCalidadDafoParteInteresada,
  createDafoControlModificacion,
  crearCalidadDafoPlantillaAnual,
  deleteCalidadDafoAnalisis,
  deleteCalidadDafoParteInteresada,
  deleteCalidadDafoYear,
  deleteDafoControlModificacion,
  updateCalidadDafoAnalisis,
  updateCalidadDafoParteInteresada,
  updateDafoControlModificacion,
} from "../api/dafo";
import { useCalidadDafo } from "../hooks/useCalidadDafo";
import {
  createEmptyAnalisisForm,
  createEmptyParteInteresadaForm,
  DAFO_BLOQUES,
  DAFO_TIPOS,
  defaultDafoFilters,
  mapAnalisisToPayload,
  mapParteInteresadaToPayload,
  type CalidadDafoAnalisis,
  type CalidadDafoAnalisisPayload,
  type CalidadDafoParteInteresada,
  type CalidadDafoParteInteresadaPayload,
} from "../types/dafo";

type Props = {
  onBack: () => void;
};

type Tab = "analisis" | "partes" | "control";
type FormMode = "create" | "edit";

type AnalisisFormState =
  | { open: false }
  | { open: true; mode: FormMode; row: CalidadDafoAnalisis | null };

type ParteFormState =
  | { open: false }
  | { open: true; mode: FormMode; row: CalidadDafoParteInteresada | null };

type ControlFormState =
  | { open: false }
  | { open: true; mode: FormMode; row: CalidadControlModificacion | null };

type DeleteState =
  | { open: false }
  | {
      open: true;
      kind: "analisis" | "parte" | "control" | "year";
      id: number;
      title: string;
      description: string;
    };

type PlantillaState =
  | { open: false }
  | {
      open: true;
      sourceYear: number;
      targetYear: number;
      copyAnalisis: boolean;
      copyPartes: boolean;
    };

const tipoLabels: Record<string, string> = {
  DEBILIDAD: "Debilidades",
  FORTALEZA: "Fortalezas",
  AMENAZA: "Amenazas",
  OPORTUNIDAD: "Oportunidades",
};

const bloqueTipoMap = [
  {
    bloque: "CONTEXTO INTERNO",
    title: "Contexto interno",
    columns: ["DEBILIDAD", "FORTALEZA"],
  },
  {
    bloque: "CONTEXTO EXTERNO",
    title: "Contexto externo",
    columns: ["AMENAZA", "OPORTUNIDAD"],
  },
];

export function CalidadDafoSection({ onBack }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("analisis");
  const [filters, setFilters] = useState(defaultDafoFilters);
  const [analisisFormState, setAnalisisFormState] = useState<AnalisisFormState>({ open: false });
  const [parteFormState, setParteFormState] = useState<ParteFormState>({ open: false });
  const [controlFormState, setControlFormState] = useState<ControlFormState>({ open: false });
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [plantillaState, setPlantillaState] = useState<PlantillaState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const requestFilters = useMemo(() => ({
    anio: filters.anio,
    search: filters.search,
    parte_interesada: "",
  }), [filters.anio, filters.search]);

  const {
    analisis,
    partes_interesadas: partesInteresadas,
    control_modificaciones: controlModificaciones,
    partes_resumen: partesResumen,
    meta,
    loading,
    error,
    reload,
  } = useCalidadDafo(requestFilters, true);

  const selectedYear = filters.anio ? Number(filters.anio) : meta.default_source_year ?? meta.current_year;
  const selectedParteName = filters.parte_interesada;

  const analysisByType = useMemo(() => {
    const grouped = new Map<string, CalidadDafoAnalisis[]>();

    for (const row of analisis) {
      const typeRows = grouped.get(row.tipo) ?? [];
      typeRows.push(row);
      grouped.set(row.tipo, typeRows);
    }

    for (const rows of grouped.values()) {
      rows.sort((a, b) => a.codigo.localeCompare(b.codigo, "es", { numeric: true }));
    }

    return grouped;
  }, [analisis]);

  const visiblePartes = selectedParteName === ""
    ? partesInteresadas
    : partesInteresadas.filter((row) => row.parte_interesada === selectedParteName);

  const setYearFilter = (value: string) => {
    setFilters((current) => ({ ...current, anio: value }));
  };

  const setSearchFilter = (value: string) => {
    setFilters((current) => ({ ...current, search: value }));
  };

  const setParteFilter = (value: string) => {
    setFilters((current) => ({ ...current, parte_interesada: value }));
  };

  useEffect(() => {
    if (
      !loading
      && selectedParteName !== ""
      && !partesResumen.some((row) => row.parte_interesada === selectedParteName)
    ) {
      setParteFilter("");
    }
  }, [loading, partesResumen, selectedParteName]);

  const openPlantillaModal = () => {
    setFormError(null);
    setPlantillaState({
      open: true,
      sourceYear: meta.default_source_year ?? selectedYear,
      targetYear: meta.suggested_new_year,
      copyAnalisis: true,
      copyPartes: true,
    });
  };

  const handleCreatePlantilla = async () => {
    if (!plantillaState.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      await crearCalidadDafoPlantillaAnual({
        source_year: plantillaState.sourceYear,
        target_year: plantillaState.targetYear,
        copy_analisis: plantillaState.copyAnalisis,
        copy_partes_interesadas: plantillaState.copyPartes,
      });

      setPlantillaState({ open: false });
      setFilters((current) => ({ ...current, anio: String(plantillaState.targetYear), parte_interesada: "" }));
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo crear la plantilla anual."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnalisis = async (payload: CalidadDafoAnalisisPayload) => {
    if (!analisisFormState.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (analisisFormState.mode === "create") {
        await createCalidadDafoAnalisis(payload);
      } else if (analisisFormState.row) {
        await updateCalidadDafoAnalisis(analisisFormState.row.id, payload);
      }

      setAnalisisFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar el registro DAFO."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitParte = async (payload: CalidadDafoParteInteresadaPayload) => {
    if (!parteFormState.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (parteFormState.mode === "create") {
        await createCalidadDafoParteInteresada(payload);
      } else if (parteFormState.row) {
        await updateCalidadDafoParteInteresada(parteFormState.row.id, payload);
      }

      setParteFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar la parte interesada."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitControl = async (payload: CalidadControlModificacionPayload) => {
    if (!controlFormState.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (controlFormState.mode === "create") {
        await createDafoControlModificacion(payload);
      } else if (controlFormState.row) {
        await updateDafoControlModificacion(controlFormState.row.id, payload);
      }

      setControlFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar la modificación del documento."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.open) {
      return;
    }

    setDeleting(true);

    try {
      if (deleteState.kind === "analisis") {
        await deleteCalidadDafoAnalisis(deleteState.id);
      } else if (deleteState.kind === "parte") {
        await deleteCalidadDafoParteInteresada(deleteState.id);
        setParteFilter("");
      } else {
        if (deleteState.kind === "year") {
          await deleteCalidadDafoYear(deleteState.id);
          setFilters((current) => ({ ...current, anio: "", parte_interesada: "" }));
        } else {
          await deleteDafoControlModificacion(deleteState.id);
        }
      }

      setDeleteState({ open: false });
      await reload();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="calidad-section dafo-section">
      <Iso900114001SectionHeader
        eyebrow="Contexto anual"
        title="DAFO / Partes interesadas"
        description="Mantiene el análisis DAFO y las partes interesadas por año. Cada nueva plantilla puede partir del año anterior."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard icon={LayoutGrid} label="DAFO" value={loading ? "..." : analisis.length} />
        <Iso900114001OverviewCard icon={Users} label="Partes interesadas" value={loading ? "..." : meta.partes_interesadas_total} />
        <Iso900114001OverviewCard
          icon={FileClock}
          label="Vista"
          value={activeTab === "analisis" ? "DAFO" : activeTab === "partes" ? "Partes" : "Documento"}
        />
        <Iso900114001OverviewCard icon={CalendarDays} label="Año" value={filters.anio || "Todos"} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "analisis", label: "DAFO", count: analisis.length },
          { id: "partes", label: "Partes interesadas", count: meta.partes_interesadas_total },
          {
            id: "control",
            label: "Control documental",
            count: controlModificaciones.length,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas DAFO"
      />

      <div className="dafo-filter-panel">
        <label className="dafo-filter-field">
          <span>Año</span>
          <select value={filters.anio} onChange={(event) => setYearFilter(event.target.value)}>
            <option value="">Todos</option>
            {meta.years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>

        <label className="dafo-filter-field dafo-search-field">
          <span>Buscar</span>
          <div className="dafo-search-input">
            <Search size={15} />
            <input value={filters.search} onChange={(event) => setSearchFilter(event.target.value)} placeholder="Código, tipo, texto, requisito..." />
          </div>
        </label>

        <div className="dafo-filter-actions">
          <button type="button" className="calidad-secondary-button" onClick={() => void reload()}>
            <RefreshCw size={15} />
            Recargar
          </button>
          <button
            type="button"
            className="dafo-danger-button"
            disabled={!filters.anio}
            onClick={() => {
              if (!filters.anio) return;
              const year = Number(filters.anio);
              setDeleteState({
                open: true,
                kind: "year",
                id: year,
                title: `Eliminar datos del año ${year}`,
                description: `Se eliminarán todos los registros de DAFO y Partes interesadas del año ${year}. El control documental se mantendrá intacto.`,
              });
            }}
          >
            <Trash2 size={15} />
            Eliminar año
          </button>
          <button type="button" className="calidad-primary-button" onClick={openPlantillaModal}>
            <Plus size={15} />
            Crear plantilla anual
          </button>
        </div>
      </div>

      {activeTab === "analisis" ? (
        <section className="dafo-panel">
          <div className="dafo-panel-toolbar">
            <div>
              <div className="calidad-toolbar-eyebrow">Análisis anual</div>
              <h3 className="calidad-toolbar-title">Matriz DAFO</h3>
            </div>
            <div className="calidad-toolbar-meta">
              <span className="calidad-toolbar-count">{loading ? "Cargando..." : `${analisis.length} registros`}</span>
              <button type="button" className="calidad-primary-button" onClick={() => { setFormError(null); setAnalisisFormState({ open: true, mode: "create", row: null }); }}>
                <Plus size={15} />
                Nuevo registro
              </button>
            </div>
          </div>
          <FeedbackState loading={loading} error={error} onRetry={() => void reload()} empty={analisis.length === 0} emptyText="No hay registros DAFO." />
          {!loading && !error && analisis.length > 0 ? (
            <div className="dafo-excel-layout">
              {bloqueTipoMap.map((block) => (
                <div className="dafo-matrix-block" key={block.bloque}>
                  <div className="dafo-matrix-block-title">{block.title}</div>
                  <div className="dafo-matrix-grid">
                    {block.columns.map((type) => (
                      <div className="dafo-matrix-column" key={type}>
                        <div className={`dafo-matrix-column-title dafo-type-${type.toLowerCase()}`}>{tipoLabels[type]}</div>
                        <div className="dafo-matrix-column-scroll">
                          {(analysisByType.get(type) ?? []).map((row) => (
                            <article className="dafo-matrix-item" key={row.id}>
                              <div className="dafo-matrix-item-header">
                                <span>{row.codigo}</span>
                                <div className="calidad-table-actions-row">
                                  <button type="button" className="calidad-icon-button" title="Editar" onClick={() => { setFormError(null); setAnalisisFormState({ open: true, mode: "edit", row }); }}>
                                    <Edit3 size={14} />
                                  </button>
                                  <button type="button" className="calidad-icon-button danger" title="Eliminar" onClick={() => setDeleteState({ open: true, kind: "analisis", id: row.id, title: "Eliminar registro DAFO", description: `Se eliminará ${row.codigo}.` })}>
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                              <p>{row.descripcion || "-"}</p>
                            </article>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : activeTab === "partes" ? (
        <section className="dafo-panel">
          <FeedbackState
            loading={loading && partesInteresadas.length === 0}
            error={error}
            onRetry={() => void reload()}
            empty={!loading && meta.partes_interesadas_total === 0}
            emptyText="No hay partes interesadas registradas."
          />
          {!error && (meta.partes_interesadas_total > 0 || partesResumen.length > 0) ? (
            <Iso900114001MasterDetailTable
              eyebrow="Partes interesadas"
              title="Necesidades, expectativas y requisitos"
              countLabel={`${visiblePartes.length} registros visibles`}
              createLabel="Nuevo registro"
              items={[
                {
                  id: "all",
                  label: "Todas las partes",
                  shortLabel: "Todas",
                  title: "Todas las partes interesadas",
                  count: meta.partes_interesadas_total,
                },
                ...partesResumen.map((row) => ({
                  id: row.parte_interesada,
                  label: row.parte_interesada,
                  shortLabel: row.parte_interesada.slice(0, 2).toUpperCase(),
                  title: row.parte_interesada,
                  count: row.total,
                })),
              ]}
              selectedId={filters.parte_interesada || "all"}
              loading={loading && partesInteresadas.length === 0}
              emptyText="No hay partes interesadas registradas."
              onCreate={() => {
                setFormError(null);
                setParteFormState({ open: true, mode: "create", row: null });
              }}
              onSelect={(id) => setParteFilter(id === "all" ? "" : String(id))}
            >
              <div className="dafo-table-scroll">
                <table className="calidad-table dafo-table">
                  <thead>
                    <tr>
                      <th className="dafo-actions-col">Acciones</th>
                      <th>Año</th>
                      <th>Parte interesada</th>
                      <th>Tipo</th>
                      <th>Detalle</th>
                      <th>Requisitos</th>
                      <th>Seguimiento primer semestre</th>
                      <th>Seguimiento segundo semestre</th>
                      <th>Evaluación de la eficacia de las acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePartes.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="dafo-loading-cell">
                          No hay registros para la parte interesada seleccionada.
                        </td>
                      </tr>
                    ) : visiblePartes.map((row) => (
                      <tr key={row.id}>
                        <td className="dafo-actions-col">
                          <div className="calidad-table-actions-row">
                            <button
                              type="button"
                              className="calidad-icon-button"
                              title="Editar"
                              aria-label="Editar parte interesada"
                              onClick={() => {
                                setFormError(null);
                                setParteFormState({ open: true, mode: "edit", row });
                              }}
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              type="button"
                              className="calidad-icon-button danger"
                              title="Eliminar"
                              aria-label="Eliminar parte interesada"
                              onClick={() => setDeleteState({
                                open: true,
                                kind: "parte",
                                id: row.id,
                                title: "Eliminar parte interesada",
                                description: `Se eliminará el registro de ${row.parte_interesada || "parte interesada"}.`,
                              })}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                        <td>{row.anio}</td>
                        <td className="iso9001-long-column">
                          <Iso900114001ScrollableCell title={row.parte_interesada ?? undefined}>
                            {row.parte_interesada || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-long-column">
                          <Iso900114001ScrollableCell title={row.tipo ?? undefined}>
                            {row.tipo || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={row.detalle ?? undefined}>
                            {row.detalle || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={row.requisitos ?? undefined}>
                            {row.requisitos || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={row.seguimiento_primer_semestre ?? undefined}>
                            {row.seguimiento_primer_semestre || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={row.seguimiento_segundo_semestre ?? undefined}>
                            {row.seguimiento_segundo_semestre || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={row.evaluacion_eficacia_acciones ?? undefined}>
                            {row.evaluacion_eficacia_acciones || "-"}
                          </Iso900114001ScrollableCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Iso900114001MasterDetailTable>
          ) : null}
        </section>
      ) : (
        <CalidadControlModificacionesTable
          rows={controlModificaciones}
          loading={loading}
          error={error}
          onRetry={() => void reload()}
          onCreate={() => { setFormError(null); setControlFormState({ open: true, mode: "create", row: null }); }}
          onEdit={(row) => { setFormError(null); setControlFormState({ open: true, mode: "edit", row }); }}
          onDelete={(row) => setDeleteState({ open: true, kind: "control", id: row.id, title: "Eliminar modificación", description: `Se eliminará la modificación registrada en ${row.fecha}.` })}
        />
      )}

      <DafoAnalisisFormModal
        open={analisisFormState.open}
        mode={analisisFormState.open ? analisisFormState.mode : "create"}
        initialValues={analisisFormState.open && analisisFormState.row ? mapAnalisisToPayload(analisisFormState.row) : createEmptyAnalisisForm(selectedYear)}
        submitting={submitting}
        error={analisisFormState.open ? formError : null}
        onClose={() => setAnalisisFormState({ open: false })}
        onSubmit={handleSubmitAnalisis}
      />

      <DafoParteFormModal
        open={parteFormState.open}
        mode={parteFormState.open ? parteFormState.mode : "create"}
        initialValues={parteFormState.open && parteFormState.row ? mapParteInteresadaToPayload(parteFormState.row) : createEmptyParteInteresadaForm(selectedYear, filters.parte_interesada)}
        submitting={submitting}
        error={parteFormState.open ? formError : null}
        onClose={() => setParteFormState({ open: false })}
        onSubmit={handleSubmitParte}
      />

      <CalidadControlModificacionFormModal
        open={controlFormState.open}
        mode={controlFormState.open ? controlFormState.mode : "create"}
        initialValues={controlFormState.open ? controlFormState.row : null}
        submitting={submitting}
        error={controlFormState.open ? formError : null}
        onClose={() => setControlFormState({ open: false })}
        onSubmit={handleSubmitControl}
      />

      <DafoPlantillaModal
        state={plantillaState}
        years={meta.years}
        submitting={submitting}
        error={plantillaState.open ? formError : null}
        onClose={() => setPlantillaState({ open: false })}
        onChange={setPlantillaState}
        onConfirm={handleCreatePlantilla}
      />

      <CalidadControlModificacionDeleteModal
        open={deleteState.open}
        title={deleteState.open ? deleteState.title : ""}
        description={deleteState.open ? deleteState.description : ""}
        deleting={deleting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={handleDelete}
      />
    </section>
  );
}

function FeedbackState({ loading, error, empty, emptyText, onRetry }: { loading: boolean; error: string | null; empty: boolean; emptyText: string; onRetry: () => void }) {
  if (error) {
    return (
      <div className="calidad-feedback-state reqleg-feedback-compact">
        <div className="calidad-feedback-title">No se pudo cargar la tabla</div>
        <p className="calidad-feedback-copy">{error}</p>
        <button type="button" onClick={onRetry} className="calidad-feedback-action">
          <RefreshCw size={15} />
          Reintentar
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="calidad-feedback-state reqleg-feedback-compact">
        <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
        <div className="calidad-feedback-title">Cargando datos</div>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="calidad-feedback-state reqleg-feedback-compact">
        <div className="calidad-feedback-title">{emptyText}</div>
      </div>
    );
  }

  return null;
}


function DafoAnalisisFormModal({ open, mode, initialValues, submitting, error, onClose, onSubmit }: { open: boolean; mode: FormMode; initialValues: CalidadDafoAnalisisPayload; submitting: boolean; error: string | null; onClose: () => void; onSubmit: (values: CalidadDafoAnalisisPayload) => Promise<void> }) {
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSubmit(values);
  };

  return (
    <div className="dafo-modal-backdrop" role="presentation">
      <form className="dafo-modal dafo-modal-wide" onSubmit={handleSubmit}>
        <div className="dafo-modal-header">
          <div>
            <div className="calidad-toolbar-eyebrow">DAFO</div>
            <h3>{mode === "create" ? "Nuevo registro DAFO" : "Editar registro DAFO"}</h3>
          </div>
          <button type="button" className="calidad-icon-button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="dafo-form-grid">
          <InputField label="Año" type="number" value={String(values.anio)} onChange={(value) => setValues((current) => ({ ...current, anio: Number(value) }))} />
          <SelectField label="Bloque" value={values.bloque} options={DAFO_BLOQUES} onChange={(value) => setValues((current) => ({ ...current, bloque: value }))} />
          <SelectField label="Tipo" value={values.tipo} options={DAFO_TIPOS} onChange={(value) => setValues((current) => ({ ...current, tipo: value }))} />
          <InputField label="Código" value={values.codigo} onChange={(value) => setValues((current) => ({ ...current, codigo: value }))} />
          <TextareaField label="Descripción" value={values.descripcion ?? ""} onChange={(value) => setValues((current) => ({ ...current, descripcion: value }))} className="dafo-field-span-2" />
        </div>
        {error ? <p className="dafo-form-error">{error}</p> : null}
        <ModalActions submitting={submitting} onClose={onClose} />
      </form>
    </div>
  );
}

function DafoParteFormModal({ open, mode, initialValues, submitting, error, onClose, onSubmit }: { open: boolean; mode: FormMode; initialValues: CalidadDafoParteInteresadaPayload; submitting: boolean; error: string | null; onClose: () => void; onSubmit: (values: CalidadDafoParteInteresadaPayload) => Promise<void> }) {
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSubmit(values);
  };

  return (
    <div className="dafo-modal-backdrop" role="presentation">
      <form className="dafo-modal dafo-modal-xl" onSubmit={handleSubmit}>
        <div className="dafo-modal-header">
          <div>
            <div className="calidad-toolbar-eyebrow">Partes interesadas</div>
            <h3>{mode === "create" ? "Nuevo registro" : "Editar registro"}</h3>
          </div>
          <button type="button" className="calidad-icon-button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="dafo-form-grid dafo-form-grid-3">
          <InputField label="Año" type="number" value={String(values.anio)} onChange={(value) => setValues((current) => ({ ...current, anio: Number(value) }))} />
          <InputField label="Parte interesada" value={values.parte_interesada ?? ""} onChange={(value) => setValues((current) => ({ ...current, parte_interesada: value }))} />
          <InputField label="Tipo" value={values.tipo ?? ""} onChange={(value) => setValues((current) => ({ ...current, tipo: value }))} />
          <TextareaField label="Detalle" value={values.detalle ?? ""} onChange={(value) => setValues((current) => ({ ...current, detalle: value }))} />
          <TextareaField label="Requisitos" value={values.requisitos ?? ""} onChange={(value) => setValues((current) => ({ ...current, requisitos: value }))} />
          <TextareaField label="Seguimiento primer semestre" value={values.seguimiento_primer_semestre ?? ""} onChange={(value) => setValues((current) => ({ ...current, seguimiento_primer_semestre: value }))} />
          <TextareaField label="Seguimiento segundo semestre" value={values.seguimiento_segundo_semestre ?? ""} onChange={(value) => setValues((current) => ({ ...current, seguimiento_segundo_semestre: value }))} />
          <TextareaField label="Evaluación de la eficacia de las acciones" value={values.evaluacion_eficacia_acciones ?? ""} onChange={(value) => setValues((current) => ({ ...current, evaluacion_eficacia_acciones: value }))} className="dafo-field-span-2" />
        </div>
        {error ? <p className="dafo-form-error">{error}</p> : null}
        <ModalActions submitting={submitting} onClose={onClose} />
      </form>
    </div>
  );
}

function DafoPlantillaModal({ state, years, submitting, error, onClose, onChange, onConfirm }: { state: PlantillaState; years: number[]; submitting: boolean; error: string | null; onClose: () => void; onChange: (state: PlantillaState) => void; onConfirm: () => Promise<void> }) {
  if (!state.open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onConfirm();
  };

  return (
    <div className="dafo-modal-backdrop" role="presentation">
      <form className="dafo-modal" onSubmit={handleSubmit}>
        <div className="dafo-modal-header">
          <div>
            <div className="calidad-toolbar-eyebrow">Plantilla anual</div>
            <h3>Crear plantilla anual</h3>
          </div>
          <button type="button" className="calidad-icon-button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="dafo-form-grid">
          <label className="dafo-form-field">
            <span>Año origen</span>
            <select value={state.sourceYear} onChange={(event) => onChange({ ...state, sourceYear: Number(event.target.value) })}>
              {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </label>
          <InputField label="Año nuevo" type="number" value={String(state.targetYear)} onChange={(value) => onChange({ ...state, targetYear: Number(value) })} />
          <label className="dafo-check-field">
            <input type="checkbox" checked={state.copyAnalisis} onChange={(event) => onChange({ ...state, copyAnalisis: event.target.checked })} />
            Copiar DAFO
          </label>
          <label className="dafo-check-field">
            <input type="checkbox" checked={state.copyPartes} onChange={(event) => onChange({ ...state, copyPartes: event.target.checked })} />
            Copiar partes interesadas
          </label>
        </div>
        {error ? <p className="dafo-form-error">{error}</p> : null}
        <ModalActions submitting={submitting} onClose={onClose} submitLabel="Crear plantilla" />
      </form>
    </div>
  );
}

function InputField({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <label className="dafo-form-field">
      <span>{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return (
    <label className="dafo-form-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function TextareaField({ label, value, onChange, className = "" }: { label: string; value: string; onChange: (value: string) => void; className?: string }) {
  return (
    <label className={`dafo-form-field ${className}`}>
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={5} />
    </label>
  );
}

function ModalActions({ submitting, onClose, submitLabel = "Guardar" }: { submitting: boolean; onClose: () => void; submitLabel?: string }) {
  return (
    <div className="dafo-modal-actions">
      <button type="button" className="calidad-secondary-button" onClick={onClose} disabled={submitting}>
        Cancelar
      </button>
      <button type="submit" className="calidad-primary-button" disabled={submitting}>
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        {submitLabel}
      </button>
    </div>
  );
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError(error)) {
    return fallback;
  }

  const fieldErrors = error.response?.data?.errors;
  const firstError = fieldErrors && typeof fieldErrors === "object"
    ? Object.values(fieldErrors).find(Array.isArray)?.[0]
    : null;

  if (typeof firstError === "string") {
    return firstError;
  }

  const message = error.response?.data?.message;

  return typeof message === "string" ? message : fallback;
}
